import { User, Mail, Phone, Globe, MapPin, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const TYPE_CONFIG = {
  ARCHITECT: { label: "Architect", icon: "🏛️", color: "bg-blue-100 text-blue-700" },
  INTERIOR_DESIGNER: { label: "Interior Designer", icon: "🎨", color: "bg-pink-100 text-pink-700" },
  LANDSCAPE_ARCHITECT: { label: "Landscape Architect", icon: "🌳", color: "bg-emerald-100 text-emerald-700" },
  DEVELOPER: { label: "Developer", icon: "🏗️", color: "bg-green-100 text-green-700" },
  BROKER: { label: "Broker", icon: "🤝", color: "bg-yellow-100 text-yellow-700" },
  CONSULTANT: { label: "Consultant", icon: "💼", color: "bg-purple-100 text-purple-700" },
  ENGINEER: { label: "Engineer", icon: "⚙️", color: "bg-orange-100 text-orange-700" },
  OTHER: { label: "Other", icon: "📋", color: "bg-gray-100 text-gray-700" }
};

function ContactInfo({ icon: Icon, value, href, isLink }) {
  if (!value) return null;
  
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      {isLink ? (
        <a href={href} className="text-primary hover:underline truncate" target={href?.startsWith("http") ? "_blank" : undefined} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}>
          {value}
        </a>
      ) : (
        <span className="text-muted-foreground truncate">{value}</span>
      )}
    </div>
  );
}

function CardActions({ contact, onEdit, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(contact)}>
          <Pencil className="w-4 h-4 mr-2" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(contact.id)} className="text-red-600">
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function YellowPageCard({ contact, onEdit, onDelete }) {
  const config = TYPE_CONFIG[contact.contact_type] || TYPE_CONFIG.OTHER;
  const websiteUrl = contact.website?.startsWith("http") ? contact.website : `https://${contact.website}`;
  
  return (
    <Card className="group hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{config.icon}</span>
            <Badge className={config.color}>{config.label}</Badge>
          </div>
          <CardActions contact={contact} onEdit={onEdit} onDelete={onDelete} />
        </div>
        <CardTitle className="text-lg mt-2">{contact.company_name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <ContactInfo icon={User} value={contact.primary_contact} />
        <ContactInfo icon={Mail} value={contact.email} href={`mailto:${contact.email}`} isLink />
        <ContactInfo icon={Phone} value={contact.phone} href={`tel:${contact.phone}`} isLink />
        <ContactInfo icon={Globe} value={contact.website} href={websiteUrl} isLink />
        <ContactInfo icon={MapPin} value={contact.address} />
      </CardContent>
    </Card>
  );
}
